import { useState } from "react";
import { useAuth, api } from "@/App";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { useLocale } from "@/i18n/LocaleContext";

export default function RatingsPrivacyCard() {
  const { t } = useLocale();
  const { user, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const ratingsEnabled = user?.ratings_enabled !== false;
  const showComments = user?.show_review_comments !== false;

  const save = async (fields) => {
    setSaving(true);
    try {
      const response = await api.put("/users/profile", fields);
      updateUser(response.data);
      toast.success(t("ratep.saved"));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="glass-card p-6 mt-6" data-testid="ratings-privacy-card">
      <div className="flex items-center gap-3 mb-3">
        <Star className="w-5 h-5 text-amber-400" />
        <h3 className="text-lg font-semibold">{t("ratep.title")}</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        {t("ratep.desc")}
      </p>

      <div className="space-y-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label htmlFor="ratings-toggle" className="font-medium cursor-pointer">
              {t("ratep.partLabel")}
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              {t("ratep.partDesc")}
            </p>
          </div>
          <Switch
            id="ratings-toggle"
            checked={ratingsEnabled}
            disabled={saving}
            onCheckedChange={(checked) => save({ ratings_enabled: checked })}
            data-testid="ratings-enabled-switch"
          />
        </div>

        <div className={`flex items-center justify-between gap-4 ${!ratingsEnabled ? "opacity-40 pointer-events-none" : ""}`}>
          <div>
            <Label htmlFor="comments-toggle" className="font-medium cursor-pointer">
              {t("ratep.commentsLabel")}
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              {t("ratep.commentsDesc")}
            </p>
          </div>
          <Switch
            id="comments-toggle"
            checked={showComments}
            disabled={saving || !ratingsEnabled}
            onCheckedChange={(checked) => save({ show_review_comments: checked })}
            data-testid="show-comments-switch"
          />
        </div>
      </div>
    </div>
  );
}
